"use client";

import { GovernmentEmployerPicRegistrationDto, governmentEmployerPicRegistrationSchema } from "@entities/employer";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, useMask } from "@react-input/mask";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from "@ubud-ui/react-ui-kit";
import { PropsWithChildren } from "react";
import { useForm } from "react-hook-form";

type FormProps = {
  formValue?: GovernmentEmployerPicRegistrationDto;
  onSubmit?: (data: GovernmentEmployerPicRegistrationDto) => void;
};

export function GovernmentEmployerPicRegistrationForm({ formValue, onSubmit, children }: FormProps & PropsWithChildren) {
  const form = useForm<GovernmentEmployerPicRegistrationDto>({
    resolver: zodResolver(governmentEmployerPicRegistrationSchema),
    defaultValues: {
      pic_name: formValue?.pic_name || undefined,
      pic_id_number: formValue?.pic_id_number || undefined,
      pic_email: formValue?.pic_email || undefined,
      pic_phone: formValue?.pic_phone || undefined,
    },
  });

  const handleSubmit = (data: GovernmentEmployerPicRegistrationDto) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const nikMaskOption = {
    mask: "____ ____ ____ ____",
    replacement: { _: /\d/ },
  };

  const nikMaskRef = useMask({ ...nikMaskOption });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 items-start">
            <FormField
              control={form.control}
              name="pic_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nama PIC <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <Input placeholder="Masukkan nama PIC" value={field.value || ""} onInput={(val) => field.onChange(val)} disabled />
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pic_id_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    NIK PIC <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <Input
                      type="tel"
                      inputMode="tel"
                      disabled
                      placeholder="Masukkan NIK yang valid"
                      value={format(field?.value?.toString() || "", nikMaskOption)}
                      ref={nikMaskRef}
                      onInput={(e) => {
                        const parseValue = (e.target as any)?.value?.toString()?.replaceAll(" ", "")?.replaceAll(" ", "");
                        field.onChange({
                          target: {
                            value: parseValue,
                          },
                        });
                      }}
                    />
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="pic_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Email PIC <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl className="mt-2">
                  <Input
                    type="email"
                    disabled
                    placeholder="Cth.: john.doe@company.com"
                    value={field.value || ""}
                    onInput={(e) => field.onChange(e)}
                  />
                </FormControl>
                <div className="mt-2 text-xs text-gray-500">Contoh: xxxxx@gmail.com</div>
                <FormMessage className="mt-2" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pic_phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Nomor telepon PIC <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl className="mt-2">
                  <Input
                    type="tel"
                    disabled
                    inputMode="tel"
                    value={field.value || ""}
                    placeholder="Cth.: 89639284892"
                    onInput={(e) => field.onChange(e)}
                  />
                </FormControl>
                <FormMessage className="mt-2" />
              </FormItem>
            )}
          />
        </div>

        {children}
      </form>
    </Form>
  );
}
